/**
 * useQuoteRefresh — keeps a bonding-curve quote fresh while the user has an
 * amount entered and the wallet is connected.
 *
 * Two refresh triggers run in parallel:
 *  1. Debounced (400 ms) on every user input change (amount / tab)
 *  2. Immediate on every new block via publicClient.watchBlockNumber
 *
 * Page Visibility
 * ───────────────
 * When the browser tab is hidden (`document.hidden === true`) block-triggered
 * fetches are skipped to avoid wasted RPC calls.  When the tab becomes visible
 * again, a fetch fires immediately so the quote is fresh before the user acts.
 *
 * Stale-response safety
 * ─────────────────────
 * Both paths share a single monotonic `latestRequestId` ref.  Every call to
 * `doFetch` captures the current id, and only the fetch whose id still matches
 * `latestRequestId.current` at completion is allowed to commit state.  Effect
 * cleanups increment the counter to invalidate any in-flight request before a
 * new subscription or input cycle begins.
 *
 * Returns `lastQuotedAt` (epoch-ms of the last successful fetch) and `secAgo`
 * (seconds elapsed since then, ticked every 1 s) for display in the quote box.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { PublicClient } from "viem";
import { bondingCurveMarketAbi } from "../config/bondingCurveMarketAbi";

// ── Constants (mirrors TradingPanel) ──────────────────────────────────────────

const MIN_BUY_ETH_WEI = 100_000_000_000_000n; // 0.0001 ETH

// ── Input parsers ─────────────────────────────────────────────────────────────

function parseEthInput(value: string): bigint | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = parseFloat(trimmed);
  if (!isFinite(n) || n <= 0) return null;
  try {
    const parts = trimmed.split(".");
    const whole = parts[0] ?? "0";
    const fracPadded = (parts[1] ?? "").slice(0, 18).padEnd(18, "0");
    return BigInt(whole) * 10n ** 18n + BigInt(fracPadded);
  } catch { return null; }
}

function parseTokenInput(value: string): bigint | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = parseFloat(trimmed);
  if (!isFinite(n) || n <= 0) return null;
  try {
    const parts = trimmed.split(".");
    const whole = parts[0] ?? "0";
    const fracPadded = (parts[1] ?? "").slice(0, 18).padEnd(18, "0");
    return BigInt(whole) * 10n ** 18n + BigInt(fracPadded);
  } catch { return null; }
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TradeQuote {
  tokensOut?: bigint;
  netEth?: bigint;
  fee: bigint;
}

export interface UseQuoteRefreshParams {
  amount: string;
  tab: "buy" | "sell";
  canTrade: boolean;
  publicClient: PublicClient;
  marketAddr: `0x${string}`;
  setQuote: (q: TradeQuote | null) => void;
  setQuoting: (b: boolean) => void;
}

export interface UseQuoteRefreshResult {
  /** Epoch-ms of the last successful quote fetch; null while no quote is live. */
  lastQuotedAt: number | null;
  /** Seconds elapsed since lastQuotedAt, updated every second. */
  secAgo: number;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useQuoteRefresh({
  amount,
  tab,
  canTrade,
  publicClient,
  marketAddr,
  setQuote,
  setQuoting,
}: UseQuoteRefreshParams): UseQuoteRefreshResult {
  const [lastQuotedAt, setLastQuotedAt] = useState<number | null>(null);
  const [secAgo, setSecAgo] = useState(0);

  /**
   * Monotonic counter. Every call to `doFetch` captures the value it was called
   * with; effect cleanups increment it to invalidate any in-flight requests
   * before a new cycle starts. Only the fetch whose id still matches at
   * completion is allowed to call `setQuote`/`setLastQuotedAt`/`setQuoting`.
   */
  const latestRequestId = useRef(0);

  /**
   * Stable refs so the block-watcher callback can read the latest amount/tab
   * without restarting the subscription on every keystroke.
   */
  const amountRef = useRef(amount);
  amountRef.current = amount;
  const tabRef = useRef(tab);
  tabRef.current = tab;

  // ── Shared fetch helper ────────────────────────────────────────────────────

  const doFetch = useCallback(async (
    currentAmount: string,
    currentTab: "buy" | "sell",
  ) => {
    const requestId = ++latestRequestId.current;
    setQuoting(true);
    try {
      if (currentTab === "buy") {
        const ethWei = parseEthInput(currentAmount);
        if (!ethWei || ethWei < MIN_BUY_ETH_WEI) return;
        const result = await publicClient.readContract({
          address: marketAddr,
          abi: bondingCurveMarketAbi,
          functionName: "quoteBuy",
          args: [ethWei],
        });
        if (requestId === latestRequestId.current) {
          const [tokensOut, fee] = result as [bigint, bigint];
          setQuote({ tokensOut, fee });
          setLastQuotedAt(Date.now());
        }
      } else {
        const tokensWei = parseTokenInput(currentAmount);
        if (!tokensWei || tokensWei === 0n) return;
        const result = await publicClient.readContract({
          address: marketAddr,
          abi: bondingCurveMarketAbi,
          functionName: "quoteSell",
          args: [tokensWei],
        });
        if (requestId === latestRequestId.current) {
          const [netEth, fee] = result as [bigint, bigint];
          setQuote({ netEth, fee });
          setLastQuotedAt(Date.now());
        }
      }
    } catch {
      if (requestId === latestRequestId.current) setQuote(null);
    } finally {
      if (requestId === latestRequestId.current) setQuoting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicClient, marketAddr, setQuote, setQuoting]);

  // ── Debounced user-edit path ───────────────────────────────────────────────
  // Clears the quote immediately on input change, then re-fetches after 400 ms.
  // Cleanup invalidates any in-flight fetch before the next cycle begins.

  useEffect(() => {
    setQuote(null);
    if (!amount.trim() || !canTrade) return;
    const timer = setTimeout(() => void doFetch(amount, tab), 400);
    return () => {
      latestRequestId.current++; // invalidate any in-flight fetch from this cycle
      clearTimeout(timer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, tab, canTrade, doFetch]);

  // ── Block-triggered refresh ────────────────────────────────────────────────
  // Subscribes to new block numbers via RPC polling. On each new block, if a
  // valid amount is entered AND the tab is visible, re-fetches immediately (no
  // debounce). When the tab is hidden the callback is a no-op; when it becomes
  // visible again the visibilitychange effect below fires a catch-up fetch.
  // amountRef/tabRef ensure keystrokes don't restart the subscription.
  // Cleanup invalidates any in-flight block-triggered fetch.

  useEffect(() => {
    if (!canTrade) return;
    const unwatch = publicClient.watchBlockNumber({
      poll: true,
      pollingInterval: 4_000,
      onBlockNumber: () => {
        if (typeof document !== "undefined" && document.hidden) return;
        const amt = amountRef.current;
        if (!amt.trim()) return;
        void doFetch(amt, tabRef.current);
      },
    });
    return () => {
      latestRequestId.current++; // invalidate any in-flight block-triggered fetch
      unwatch();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canTrade, publicClient, doFetch]);

  // ── Page Visibility — catch-up fetch on tab focus ──────────────────────────
  // When the tab becomes visible after being hidden, immediately re-fetch so
  // the quote is current before the user acts. No fetch is fired on hide.

  useEffect(() => {
    if (!canTrade || typeof document === "undefined") return;
    const handleVisibilityChange = () => {
      if (document.hidden) return;
      const amt = amountRef.current;
      if (!amt.trim()) return;
      void doFetch(amt, tabRef.current);
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canTrade, doFetch]);

  // ── "X seconds ago" ticker ─────────────────────────────────────────────────

  useEffect(() => {
    if (lastQuotedAt === null) { setSecAgo(0); return; }
    setSecAgo(0);
    const id = setInterval(() => {
      setSecAgo(Math.floor((Date.now() - lastQuotedAt) / 1000));
    }, 1_000);
    return () => clearInterval(id);
  }, [lastQuotedAt]);

  return { lastQuotedAt, secAgo };
}
